import { useContext, useEffect, useState } from "react";
import { componentNames } from "../../Utils/Constants";
import withRole from "../../Utils/withRole";
import SalaryComponent from "./SalaryComponent";
import { useNavigate, useParams } from "react-router-dom";
import { urlEnum } from "../../Utils/urlEnum";
import { getData } from "../../Services/getData";
import { CacheContext } from "../../Context/cacheContext";
import { Vocabulary } from "../../Utils/Vocabulary";
import { useDocumentTitle } from "../../Hooks/useDocumentTitle";

/**
 *  SalaryDashboard is a React component that displays the salary information for a specific user.
 *  It checks for user permissions and retrieves the necessary data to render the SalaryComponent.
 *  The component uses React Router's useParams to get the user ID, month, and year from the URL.
 *  It also utilizes context to access cached data and manage state.
 * @param props  - The props for the SalaryDashboard component.
 * @returns  A React element representing the salary dashboard for a user, or redirects if the user lacks permission.
 */
function SalaryDashboard(props: any) {
  const params = useParams();
  const navigate = useNavigate();
  const [hasPermission, setHasPermission] = useState(false);
  const cacheContext: any = useContext(CacheContext);
  const cacheData = cacheContext.cache;

  const accessedUser = cacheData?.users?.find(
    (user: any) => user.id == params?.id,
  );
  const monthName = params?.month
    ? new Date(0, Number(params.month) - 1, 1).toLocaleString("ro-Ro", {
        month: "long",
      })
    : null;

  /**
   * Sets the document title based on the accessed user's name, month, and year.
   */
  useDocumentTitle(
    hasPermission && accessedUser?.nume
      ? [`${accessedUser.nume} - ${Vocabulary.salary}`, monthName, params?.year]
          .filter(Boolean)
          .join(" ")
      : null,
  );

  /**
   * Checks for user permissions and manages access to the salary dashboard.
   * If the user does not have permission to view the salary information, they are redirected.
   */
  useEffect(() => {
    const userId = localStorage?.useId;
    const accessedUserId = params?.id;
    const permissions = localStorage?.permissions;
    const accessedUser = cacheData?.users.find(
      (user: any) => user.id == accessedUserId,
    );
    const roles = accessedUser?.roles;
    const isMarketing = roles?.find(
      (role: any) => role.name === Vocabulary.marketing,
    );
    const hasPermissionToseeUserSalary = permissions.includes("usersSalary");
    if (accessedUserId)
      checkForPermission(
        userId,
        accessedUserId,
        hasPermissionToseeUserSalary,
        isMarketing,
      );
    else setHasPermission(true);
  }, []);

  /**
   * Retrieves the list of managed user IDs for a given user ID.
   * @param userId
   * @returns
   */
  const getManagedUserIds = async (userId: string) => {
    const url = urlEnum.user + "/" + userId;
    const response = await getData(url);
    if (response?.data?.data) {
      return response?.data?.data[0]?.managed_user_ids;
    }
    return [];
  };

  /**
   * Checks for user permissions to view the salary dashboard and manages access accordingly.
   * @param userId
   * @param accessedUserId
   * @param hasPermissionToseeUserSalary
   */
  const checkForPermission = async (
    userId: string,
    accessedUserId: string | undefined,
    hasPermissionToseeUserSalary: boolean,
    isMarketing: any,
  ) => {
    if (accessedUserId != userId) {
      //if the user dosent have permission to see other users salary, redirect
      const managedUserIds = await getManagedUserIds(userId);
      if (!hasPermissionToseeUserSalary && accessedUserId) {
        if (accessedUserId != userId) navigate("/*");
      } else if (hasPermissionToseeUserSalary) {
        if (managedUserIds) {
          //if the user manages other users, he should only see the salary of user he manages
          const managesUser = managedUserIds.some(
            (user: any) => user == accessedUserId,
          );
          if (!managesUser && !isMarketing) navigate("/*");
        }
      }
    }
    setHasPermission(true);
  };

  return (
    hasPermission && <SalaryComponent name={componentNames.SalaryComponent} />
  );
}
export default withRole(SalaryDashboard);
